import * as React from "react";
import {
  DndContext,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, useSortable, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import arrayMove from "array-move";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/** Merge refs for dnd-kit + react-virtualized column element. */
function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>): React.RefCallback<T> {
  return (node: T | null) => {
    refs.forEach((ref) => {
      if (ref == null) return;
      if (typeof ref === "function") {
        ref(node);
      } else {
        (ref as React.MutableRefObject<T | null>).current = node;
      }
    });
  };
}

interface SortableHeaderCellProps {
  id: string;
  /** Full react-virtualized header column node (includes sort onClick on the root). */
  columnElement: React.ReactElement;
}

/**
 * Prepends a drag handle inside the column header root so we keep the original
 * sortable <div> from react-virtualized (click / keyboard), which we were
 * dropping when rendering only `columnElement.props.children`.
 */
const SortableHeaderCell: React.FC<SortableHeaderCellProps> = ({ id, columnElement: col }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const baseStyle = (col.props as { style?: React.CSSProperties }).style;
  const mergedStyle: React.CSSProperties = {
    ...baseStyle,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    display: "flex",
    alignItems: "center",
    minWidth: 0,
  };
  const originalRef = (col as React.ReactElement & { ref?: React.Ref<HTMLElement> }).ref;

  return React.cloneElement(col, {
    ref: mergeRefs(setNodeRef, originalRef),
    style: mergedStyle,
    children: (
      <>
        <span
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            cursor: "grab",
            padding: "2px 4px",
            marginRight: 4,
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
          }}
          aria-label="Drag to reorder column"
        >
          <FontAwesomeIcon icon={["fas", "grip-vertical"]} />
        </span>
        <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>{col.props.children}</div>
      </>
    ),
  });
};

interface SortableHeaderRowProps {
  className: string;
  columns: React.ReactNode[];
  style: React.CSSProperties;
  columnDataKeys: string[];
  onColumnReorder: (newOrder: string[]) => void;
}

export const SortableHeaderRow: React.FC<SortableHeaderRowProps> = ({
  className,
  columns,
  style,
  columnDataKeys,
  onColumnReorder,
}) => {
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const fromIndex = columnDataKeys.indexOf(String(active.id));
      const toIndex = columnDataKeys.indexOf(String(over.id));
      if (fromIndex < 0 || toIndex < 0) return;
      const newOrder = arrayMove(columnDataKeys, fromIndex, toIndex);
      onColumnReorder(newOrder);
    },
    [columnDataKeys, onColumnReorder]
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={columnDataKeys} strategy={horizontalListSortingStrategy}>
        <div className={className} role="row" style={style}>
          {columns.map((col, i) => {
            const dataKey = columnDataKeys[i];
            if (!dataKey) return col;
            return <SortableHeaderCell key={dataKey} id={dataKey} columnElement={col as React.ReactElement} />;
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
};
